import { useState } from 'react';
import './styles/admin.scss';
import { ApiHelper } from '../../apiHelper/ApiHelper';
import VyukaTabs from '../../components/admin/VyukaTabs';
import { downloadJson } from '../../components/admin/downloadJson';

type ImportResult = { imported: number; skipped: number; errors: string[] };

// LLM-friendly sample shown to the admin and offered as a downloadable template.
const SAMPLE = {
    version: 1,
    questions: [
        {
            subject: "physics", type: "single", topic: "Kinematika", difficulty: 3,
            text: "Jaká je jednotka zrychlení v soustavě SI?",
            explanation: "Zrychlení a = m/s².",
            options: [
                { text: "m/s²", correct: true },
                { text: "m/s", correct: false },
                { text: "kg", correct: false }
            ]
        },
        {
            subject: "biology", type: "none", topic: "Buňka", difficulty: 2,
            text: "Která z uvedených organel provádí v lidské buňce fotosyntézu?",
            explanation: "Lidská buňka fotosyntézu neprovádí – správně je „žádná z předchozích“.",
            options: [
                { text: "Mitochondrie", correct: false },
                { text: "Jádro", correct: false },
                { text: "Ribozom", correct: false }
            ]
        }
    ]
};

export default function AdminQuestionImport() {
    const [file, setFile] = useState<File | null>(null);
    const [busy, setBusy] = useState(false);
    const [result, setResult] = useState<ImportResult | null>(null);
    const api = new ApiHelper();

    async function doImport() {
        if (!file) { api.alert.error("Vyber soubor JSON"); return; }
        setBusy(true);
        setResult(null);
        try {
            const text = await file.text();
            let parsed: any;
            try { parsed = JSON.parse(text); } catch { api.alert.error("Soubor není platný JSON"); setBusy(false); return; }
            // Accept both the {version, questions} envelope and a bare array of questions.
            const payload = Array.isArray(parsed) ? { version: 1, questions: parsed } : parsed;
            const res: ImportResult = await (await api.adminContentApi.importQuestions(payload)).json();
            setResult(res);
            if (res.imported > 0) api.alert.success(`Naimportováno ${res.imported} otázek (čekají na kontrolu)`);
            else api.alert.error("Nebyla naimportována žádná otázka");
        } catch (x: any) { api.adminError(x); }
        finally { setBusy(false); }
    }

    return (
        <div>
            <div className="adminHead">
                <div><h1>Výuka</h1><p>Import otázek z JSON – naimportované otázky čekají na kontrolu a nepoužívají se v testech</p></div>
            </div>

            <VyukaTabs />

            <div className="aCard" style={{ marginBottom: 18 }}>
                <h2>Nahrát soubor</h2>
                <div className="uploadField" style={{ marginBottom: 12 }}>
                    <input type="file" accept=".json,application/json" onChange={e => { setFile(e.target.files?.[0] ?? null); setResult(null); }} />
                </div>
                <div className="aActions">
                    <button className="aBtn" onClick={doImport} disabled={busy || !file}>{busy ? "Importuji…" : "Importovat"}</button>
                    <button className="aBtn ghost" onClick={() => downloadJson("otazky-vzor.json", SAMPLE)}>Stáhnout vzor</button>
                </div>

                {result && (
                    <div style={{ marginTop: 16 }}>
                        <p><span className="ok">Naimportováno: {result.imported}</span>{result.skipped > 0 && <> · <span className="no">přeskočeno: {result.skipped}</span></>}</p>
                        {result.errors.length > 0 && (
                            <ul className="importErrors">
                                {result.errors.map((e, i) => <li key={i}>{e}</li>)}
                            </ul>
                        )}
                    </div>
                )}
            </div>

            <div className="aCard">
                <h2>Formát souboru</h2>
                <p className="noneHint" style={{ marginTop: 0 }}>
                    Pole <code>subject</code>: physics / chemistry / biology. Pole <code>type</code>: single (jedna správná) /
                    multiple (více správných) / none (žádná z uvedených – možnost „žádná z předchozích“ neuváděj, doplní ji systém).
                    Pole <code>difficulty</code> 1–5. U typu <code>none</code> mají všechny možnosti <code>correct: false</code>.
                </p>
                <pre className="codeBlock">{JSON.stringify(SAMPLE, null, 2)}</pre>
            </div>
        </div>
    );
}
